// Shared leaderboard for the roller-ski game. Every finished run is a doc in
// skiRuns, so a person can hold several rows. Personal bests stay in
// skiEndless (one doc per uid) and skiGuest (one doc per device); bests from
// before skiRuns existed are shown from there. A new name only applies to
// the run just played and later ones. See firestore.rules.
import { db } from "../../FIREBASE/firebase";

// `uid` is whose run it is: a player's uid, or "g:" + a guest's device id
export interface Score { id: string; uid: string; name: string; score: number; guest: boolean }

const scores = () => db.collection("skiEndless");
const guests = () => db.collection("skiGuest");
const runs = () => db.collection("skiRuns");

const GUEST_ID_KEY = "skigame-guest-id";
const GUEST_NAME_KEY = "skigame-guest-name";
const PLAYER_NAME_KEY = "skigame-name-";
export const GUEST_NAME_MAX = 16;

export async function topScores(n = 10): Promise<Score[]> {
  const [a, b, r] = await Promise.all([
    scores().orderBy("score", "desc").limit(n).get(),
    guests().orderBy("score", "desc").limit(n).get(),
    runs().orderBy("score", "desc").limit(n).get(),
  ]);
  const name = (d: { get: (k: string) => unknown }) => capital(String(d.get("name") ?? "Anonym"));
  const all: Score[] = r.docs.map((d) => {
    const who = String(d.get("who"));
    return { id: d.id, uid: who, name: name(d), score: Number(d.get("score")), guest: who.startsWith("g:") };
  });
  // A best is also a run once it was set after skiRuns came; only older bests are added
  const seen = new Set(all.map((s) => `${s.uid}:${s.score}`));
  const bests = (snap: typeof a, guest: boolean) =>
    snap.docs.forEach((d) => {
      const uid = (guest ? "g:" : "") + d.id;
      const score = Number(d.get("score"));
      if (!seen.has(`${uid}:${score}`)) all.push({ id: "best:" + uid, uid, name: name(d), score, guest });
    });
  bests(a, false);
  bests(b, true);
  return all.sort((x, y) => y.score - x.score).slice(0, n);
}

export async function myBest(uid: string): Promise<number | null> {
  const d = await scores().doc(uid).get();
  return d.exists ? Number(d.get("score")) : null;
}

// The name a signed-in player has on the list, if they have a row yet
export async function myName(uid: string): Promise<string | null> {
  const picked = store.get(PLAYER_NAME_KEY + uid);
  if (picked) return cleanName(picked);
  const d = await scores().doc(uid).get();
  return d.exists ? cleanName(String(d.get("name") ?? "")) || null : null;
}

// Saves the score if it beats the player's previous best; returns whether it did
export async function submitScore(uid: string, name: string, score: number): Promise<boolean> {
  const best = await myBest(uid);
  if (best !== null && best >= score) return false;
  await scores().doc(uid).set({ name: cleanName(name), score, at: Date.now() });
  return true;
}

// Saves one finished run as its own row on the list; returns its id
export async function submitRun(who: string, name: string, score: number): Promise<string | null> {
  score = Math.floor(score);
  if (score < 1) return null;
  const ref = await runs().add({ who, name: cleanName(name) || "Anonym", score, at: Date.now() });
  return ref.id;
}

// Puts a new name on one run, e.g. a friend's run saved under your name.
// Earlier runs keep the name they were played under.
export async function renameRun(id: string, name: string): Promise<void> {
  await runs().doc(id).update({ name: cleanName(name) });
}

// The name a signed-in player's next runs are saved under, on this device
export const renamePlayer = (uid: string, name: string): void => store.set(PLAYER_NAME_KEY + uid, cleanName(name));

// ── guests ──
const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      // private mode: works for this visit only
    }
  },
};

let sessionId: string | null = null;
// Random per-device id; it is what ties a guest's runs together
export function guestId(): string {
  let id = store.get(GUEST_ID_KEY) ?? sessionId;
  if (!id) {
    const b = new Uint8Array(12);
    crypto.getRandomValues(b);
    id = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    store.set(GUEST_ID_KEY, id);
  }
  sessionId = id;
  return id;
}

// Names start with a capital letter, both when saved and when shown
const capital = (s: string): string => s.charAt(0).toLocaleUpperCase("sv-SE") + s.slice(1);
export const cleanName = (s: string): string => capital(s.replace(/\s+/g, " ").trim().slice(0, GUEST_NAME_MAX));
export const guestName = (): string => cleanName(store.get(GUEST_NAME_KEY) ?? "");

export async function guestBest(): Promise<number | null> {
  const d = await guests().doc(guestId()).get();
  return d.exists ? Number(d.get("score")) : null;
}

// Saves a guest run under `name` (remembered on this device) if it is a record
export async function submitGuest(name: string, score: number): Promise<boolean> {
  name = cleanName(name);
  store.set(GUEST_NAME_KEY, name);
  const ref = guests().doc(guestId());
  const d = await ref.get();
  const best = d.exists ? Number(d.get("score")) : null;
  const isNew = best === null || score > best;
  if (!isNew) return false;
  await ref.set({ name, score, at: Date.now() });
  return true;
}

// The name this device's next guest runs are saved under
export const renameGuest = (name: string): void => store.set(GUEST_NAME_KEY, cleanName(name));

// Only the first name is shown on the list
export const displayName = (name: string | null | undefined, email: string | null | undefined): string =>
  (name || "").trim().split(/\s+/)[0] || (email || "").split("@")[0].split(/[._]/)[0] || "Anonym";

export const fmtScore = (n: number): string => Math.floor(n).toLocaleString("sv-SE");
